/* global RTCSessionDescription, RTCIceCandidate, RTCPeerConnection */

import SdpHelpers from '../utils/SdpHelpers';
import Logger from '../utils/Logger';
import Pair from '../Pair';
import VcxEvent from '../vcxEventProperties';
import customEvents from './../customEvents';
import EL from './../EventLogger';
import enxConfig from './../../Common.config.js';

const BaseStack = (specInput) => {
    const that = {};
    const specBase = specInput;
    let localDesc;
    that.remoteDesc = undefined;
    Logger.info('Starting Base stack', specBase);

    that.pcConfig = {
        iceServers: [],
    };

    that.con = {};
    if (specBase.iceServers !== undefined) {
        that.pcConfig.iceServers = specBase.iceServers;
    }
    if (specBase.forceTurn === true) {
        that.pcConfig.iceTransportPolicy = 'relay';
    }
    if (specBase.audio === undefined) {
        specBase.audio = true;
    }
    if (specBase.video === undefined) {
        specBase.video = true;
    }
    specBase.remoteCandidates = [];
    specBase.localCandidates = [];
    specBase.remoteDescriptionSet = false;

    that.mediaConstraints = {
        offerToReceiveVideo: (specBase.video !== undefined && specBase.video !== false),
        offerToReceiveAudio: (specBase.audio !== undefined && specBase.audio !== false),
    };

    function getPlugin() {
        return document.getElementById('WebrtcEverywherePluginId');
    }

    const IERTCSessionDescription = function (RTCSessionDescriptionInit) {
        return getPlugin().createSessionDescription(RTCSessionDescriptionInit);
    };

    const IERTCPeerConnection = function (configuration, constraints) {
        return getPlugin().createPeerConnection(configuration, constraints);
    };

    // creating peer connection object
    if (Pair.browserEngineCheck() === 'IE') {
        that.peerConnection = IERTCPeerConnection(that.pcConfig, that.con);
    } else {
        that.peerConnection = new RTCPeerConnection(that.pcConfig, that.con);
    }

    const gotDescriptionLocalForPublish = function (desc) {
        localDesc = desc;
        localDesc.sdp = localDesc.sdp.replace('a=sendrecv', 'a=sendonly');
        setLocalDescForOffer(false, desc);
    };

    const gotDescriptionLocalForSubscribe = function (desc) {
        localDesc = desc;
        setLocalDescForOffer(true, desc);
    };

    function maybeFixConfiguration(pcConfig) {
        if (!pcConfig) {
            return;
        }
        for (let i = 0; i < pcConfig.iceServers.length; i++) {
            if (pcConfig.iceServers[i].hasOwnProperty('urls')) {
                pcConfig.iceServers[i].url = pcConfig.iceServers[i].urls;
                delete pcConfig.iceServers[i].urls;
            }
        }
    }

    // Aux functions
    const errorCallback = (where, errorcb, message) => {
        Logger.error('message:', message, 'in baseStack at', where);
        if (errorcb !== undefined) {
            errorcb('error');
        }
    };

    const successCallback = (message) => {
        Logger.info('Success in BaseStack', message);
    };

    const onIceCandidate = (event) => {
        let candidateObject = {};
        const candidate = event.candidate;
        if (!candidate) {
            Logger.info('Gathered all candidates. Sending END candidate');
            candidateObject = {
                sdpMLineIndex: -1,
                sdpMid: 'end',
                candidate: 'end',
            };
        } else {
            /* if (!candidate.candidate.match(/a=/)) {
                 candidate.candidate = `a=${candidate.candidate}`;
             }*/

            candidateObject = {
                sdpMLineIndex: candidate.sdpMLineIndex,
                sdpMid: candidate.sdpMid,
                candidate: `a=${candidate.candidate}`,
            };
        }

        if (specBase.remoteDescriptionSet) {
            specBase.callback({ type: 'candidate', candidate: candidateObject });
        } else {
            specBase.localCandidates.push(candidateObject);
            Logger.info('Storing candidate: ', specBase.localCandidates.length, candidateObject);
        }
    };

    const setLocalDescForOffer = (isSubscribe, sessionDescription) => {
        localDesc = sessionDescription;
        if (!isSubscribe) {
            localDesc.sdp = that.enableSimulcast(localDesc.sdp);
        }
        if (!isSubscribe && (Pair.browserEngineCheck() === 'safari')) {
            localDesc.sdp = localDesc.sdp.replace('a=sendrecv', 'a=sendonly');
        }
        localDesc.sdp = SdpHelpers.setMaxBW(localDesc.sdp, specBase);
        specBase.callback({
            type: localDesc.type,
            sdp: localDesc.sdp,
        });
        //EL.info('webrtc-stack-event', customEvents.event_set_local_description, {localDesc: localDesc});
    };
    function getCodecFromMedia(medias, mediaType, codecName) {
        for (const media of medias) {
            if (media.media.toLowerCase() === mediaType.toLowerCase()) {
                for (const codec of media.codecs) {
                    if (codec.name.toLowerCase() === codecName.toLowerCase()) {
                        return codec;
                    }
                }
            }
        }
    }
    const getSDP = function (sdp, type) {
        let codecToRemove;
        let codecToKeep;
        const msg = new Object();
        msg.sdp = sdp.sdp;
        msg.type = sdp.type;
        if (type === VcxEvent.constant.H264_CODEC) {
            codecToRemove = getCodecFromMedia(sdp.media, 'video', 'vp8');
            codecToKeep = getCodecFromMedia(sdp.media, 'video', 'h264');
        } else if (type === VcxEvent.constant.default) {
            codecToRemove = getCodecFromMedia(sdp.media, 'video', 'h264');
            codecToKeep = getCodecFromMedia(sdp.media, 'video', 'vp8');
            if (codecToRemove != undefined) {
                msg.sdp = msg.sdp.replace(`a=fmtp:${codecToRemove.payload} level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f\r\n`, '');
            }
        } else {
            return msg;
        }
        if (codecToRemove != undefined) {
            msg.sdp = msg.sdp.replace(`a=rtpmap:${codecToRemove.payload} ${codecToRemove.name}/90000\r\n`, '');
            msg.sdp = msg.sdp.replace(`m=video 1 UDP/TLS/RTP/SAVPF ${codecToKeep.payload} ${codecToRemove.payload}\r\n`, `m=video 1 UDP/TLS/RTP/SAVPF ${codecToKeep.payload}\r\n`);
            msg.sdp = msg.sdp.replace(`m=video 1 UDP/TLS/RTP/SAVPF ${codecToRemove.payload} ${codecToKeep.payload}\r\n`, `m=video 1 UDP/TLS/RTP/SAVPF ${codecToKeep.payload}\r\n`);
            msg.sdp = msg.sdp.replace(`a=rtcp-fb:${codecToRemove.payload} ccm fir\r\n`, '');
            msg.sdp = msg.sdp.replace(`a=rtcp-fb:${codecToRemove.payload} nack\r\n`, '');
            msg.sdp = msg.sdp.replace(`a=rtcp-fb:${codecToRemove.payload} nack pli\r\n`, '');
            msg.sdp = msg.sdp.replace(`a=rtcp-fb:${codecToRemove.payload} goog-remb\r\n`, '');
        }
        return msg;
    };
    that.resetSDP = function (mediaConfiguration) {
        const msg = getSDP(that.remoteDesc, mediaConfiguration);
        that.peerConnection.createOffer({ offerToReceiveVideo: true, offerToReceiveAudio: true, iceRestart: false }).then((offer) => {
            Logger.info(`OFFER: - \n${offer.sdp}`);
            Logger.info(`RECVD ANSWER B4: - \n${that.remoteDesc.sdp}`);
            that.peerConnection.setLocalDescription(offer).then(() => {
                Logger.info(`ANSWER: - \n${msg.sdp}`);
                that.peerConnection.setRemoteDescription(new RTCSessionDescription(msg));
            }).catch(errorCallback.bind(null, 'processAnswer', undefined));
        }).catch((reason) => {
            Logger.info(reason);
        });
    };
    const processAnswer = (message, mediaConfiguration) => {
        let msg = message;
        Logger.info('Set remote and local description');
        Logger.debug('Remote Description', msg.sdp);
        Logger.debug('Local Description', localDesc.sdp);
        msg.sdp = SdpHelpers.setMaxBW(msg.sdp, specBase);
        that.remoteDesc = JSON.parse(JSON.stringify(msg));
        if (Pair.browserEngineCheck() === 'IE') {
            that.peerConnection.setLocalDescription(localDesc);
            Logger.info(`Remote msg details for sdp:- ${JSON.stringify(msg)}`);
            that.peerConnection.setRemoteDescription(IERTCSessionDescription(msg));
            specBase.remoteDescriptionSet = true;
            Logger.info(
                'Candidates to be added: ', specBase.remoteCandidates.length,
                specBase.remoteCandidates,
            );
            while (specBase.remoteCandidates.length > 0) {
                // IMPORTANT: preserve ordering of candidates
                that.peerConnection.addIceCandidate(specBase.remoteCandidates.shift());
            }
            Logger.info('Local candidates to send:', specBase.localCandidates.length);
            while (specBase.localCandidates.length > 0 && !that.peerConnection) {
                // IMPORTANT: preserve ordering of candidates
                specBase.callback({ type: 'candidate', candidate: specBase.localCandidates.shift() });
            }
        } else {
            Logger.debug('Remote Description', that.remoteDesc.sdp);
            that.peerConnection.setLocalDescription(localDesc).then(() => {
                // browser === safari,   version >= 12.1, mediaConfiguration === default/vp8
                // browser === safari,  browser version <= 12.0, mediaConfig === default/vp8
                // browser !== safari, mediaConfig === h264 or VP8

                if ((enxConfig.browser_info.name === 'safari' && enxConfig.browser_info.version >= VcxEvent.constant.SAFARI_VERSION_SUPPORTING_VP8 && mediaConfiguration !== VcxEvent.constant.H264_CODEC)) {
                    msg = getSDP(msg, VcxEvent.constant.default);
                } else if ((enxConfig.browser_info.name === 'safari' && enxConfig.browser_info.version <= VcxEvent.constant.SAFARI_VERSION_NOT_SUPPORTING_VP8 && mediaConfiguration !== VcxEvent.constant.H264_CODEC)) {
                    msg = getSDP(msg, VcxEvent.constant.H264_CODEC);
                } else {
                    msg = getSDP(msg, VcxEvent.constant.default);
                }
                Logger.debug('Remote Description', that.remoteDesc.sdp);
                that.peerConnection.setRemoteDescription(new RTCSessionDescription(msg)).then(() => {
                    specBase.remoteDescriptionSet = true;
                    Logger.info(
                        'Candidates to be added: ', specBase.remoteCandidates.length,
                        specBase.remoteCandidates,
                    );
                    while (specBase.remoteCandidates.length > 0 && (!that.peerConnection || !that.peerConnection.remoteDescription.type)) {
                        // IMPORTANT: preserve ordering of candidates
                        that.peerConnection.addIceCandidate(specBase.remoteCandidates.shift());
                    }
                    Logger.info('Local candidates to send:', specBase.localCandidates.length);
                    while (specBase.localCandidates.length > 0) {
                        // IMPORTANT: preserve ordering of candidates
                        specBase.callback({ type: 'candidate', candidate: specBase.localCandidates.shift() });
                    }
                }).catch(errorCallback.bind(null, 'processAnswer', undefined));
            }).catch(errorCallback.bind(null, 'processAnswer', undefined));
        }
    };
    const IERTCIceCandidate = function (RTCIceCandidateInit) {
        return getPlugin().createIceCandidate(RTCIceCandidateInit);
    };
    const processNewCandidate = (message) => {
        const msg = message;
        try {
            let obj;
            if (typeof (msg.candidate) === 'object') {
                obj = msg.candidate;
            } else {
                obj = JSON.parse(msg.candidate);
            }
            if (obj.candidate === 'end') {
                // ignore the end candidate for chrome
                return;
            }
            obj.candidate = obj.candidate.replace('a=', '');
            obj.sdpMLineIndex = parseInt(obj.sdpMLineIndex, 10);
            const candidate = IERTCIceCandidate(obj);
            if (specBase.remoteDescriptionSet) {
                that.peerConnection.addIceCandidate(candidate);
            } else {
                specBase.remoteCandidates.push(candidate);
            }
        } catch (e) {
            Logger.error('Error parsing candidate', msg.candidate);
        }
    };

    // Peerconnection events

    that.peerConnection.onicecandidate = onIceCandidate;
    that.peerConnection.onaddstream = (stream) => {
        if (that.onaddstream) {
            that.onaddstream(stream);
        }
    };
    that.peerConnection.onremovestream = (stream) => {
        if (that.onremovestream) {
            that.onremovestream(stream);
        }
    };

    that.peerConnection.oniceconnectionstatechange = (ev) => {
        if (that.oniceconnectionstatechange) {
            Logger.info('ICE connection state changed', ev.currentTarget.iceConnectionState);
            Logger.info('ICE gathering state changed', ev.currentTarget.iceGatheringState);
            that.oniceconnectionstatechange(ev.currentTarget.iceConnectionState);
        }
    };

    // public functions

    that.enableSimulcast = (sdpInput) => {
        Logger.error('Simulcast not implemented');
        return sdpInput;
    };
    that.updateSimulcastLayersBitrate = (bitrates) => {
        if (that.simulcast) {
            that.simulcast.spatialLayerBitrates = bitrates;
            that.setSimulcastLayersBitrate();
        }
    };

    that.setSimulcastLayersBitrate = () => {
        Logger.error('Simulcast not implemented');
    };
    that.setSimulcast = (enable) => {
        that.simulcast = enable;
    };

    that.close = () => {
        that.state = 'closed';
        that.peerConnection.close();
    };

    that.updateSpec = (configInput, callback = () => { }) => {
        const config = configInput;
        if (config.maxVideoBW || config.maxAudioBW) {
            if (config.maxVideoBW) {
                Logger.debug(
                    'Maxvideo Requested:', config.maxVideoBW,
                    'limit:', specBase.limitMaxVideoBW,
                );
                if (config.maxVideoBW > specBase.limitMaxVideoBW) {
                    config.maxVideoBW = specBase.limitMaxVideoBW;
                }
                specBase.maxVideoBW = config.maxVideoBW;
                Logger.debug('Result', specBase.maxVideoBW);
            }
            if (config.maxAudioBW) {
                if (config.maxAudioBW > specBase.limitMaxAudioBW) {
                    config.maxAudioBW = specBase.limitMaxAudioBW;
                }
                specBase.maxAudioBW = config.maxAudioBW;
            }

            localDesc.sdp = SdpHelpers.setMaxBW(localDesc.sdp, specBase);
            if (config.Sdp || config.maxAudioBW) {
                Logger.debug('Updating with SDP renegotiation', specBase.maxVideoBW, specBase.maxAudioBW);
                that.peerConnection.setLocalDescription(localDesc)
                    .then(() => {
                        that.remoteDesc.sdp = SdpHelpers.setMaxBW(that.remoteDesc.sdp, specBase);
                        return that.peerConnection.setRemoteDescription(new RTCSessionDescription(that.remoteDesc));
                    }).then(() => {
                        specBase.remoteDescriptionSet = true;
                        specBase.callback({ type: 'updatestream', sdp: localDesc.sdp });
                    }).catch(errorCallback.bind(null, 'updateSpec', callback));
            } else {
                Logger.debug(
                    'Updating without SDP renegotiation, ' +
                    'newVideoBW:', specBase.maxVideoBW,
                    'newAudioBW:', specBase.maxAudioBW,
                );
                specBase.callback({ type: 'updatestream', sdp: localDesc.sdp });
            }
        }
        if (config.minVideoBW || (config.slideShowMode !== undefined) ||
            (config.muteStream !== undefined) || (config.qualityLayer !== undefined) ||
            (config.video !== undefined)) {
            Logger.debug('MinVideo Changed to ', config.minVideoBW);
            Logger.debug('SlideShowMode Changed to ', config.slideShowMode);
            Logger.debug('muteStream changed to ', config.muteStream);
            Logger.debug('Video Constraints', config.video);
            specBase.callback({ type: 'updatestream', config });
        }
    };

    that.createOffer = (isSubscribe, iceRestart) => {
        if (isSubscribe !== true) {
            that.mediaConstraints = {
                offerToReceiveVideo: false,
                offerToReceiveAudio: false,
            };
        }
        if (iceRestart) {
            that.mediaConstraints.iceRestart = iceRestart;
        }
        Logger.debug(`Creating offer :-${JSON.stringify(that.mediaConstraints)}`);
        if (Pair.browserEngineCheck() === 'IE') {
            if (isSubscribe !== true) {
                that.peerConnection.createOffer(gotDescriptionLocalForPublish, errorCallback);
            } else {
                const offerConstraints = {
                    optional: [
                        { OfferToReceiveAudio: true },
                        { OfferToReceiveVideo: true },
                    ],
                };
                that.peerConnection.createOffer(gotDescriptionLocalForSubscribe, errorCallback, offerConstraints);
            }
        } else {
            that.peerConnection.createOffer(that.mediaConstraints)
                .then(setLocalDescForOffer.bind(null, isSubscribe))
                .catch(errorCallback.bind(null, 'Create Offer', undefined));
        }
    };

    that.addStream = (stream) => {
        that.peerConnection.addStream(stream);
    };
    that.addTrack = (track) => {
        that.peerConnection.addTrack(track);
    };

    that.processSignalingMessage = (msgInput, mediaConfiguration) => {
        if (msgInput.type === 'answer') {
            processAnswer(msgInput, mediaConfiguration);
        } else if (msgInput.type === 'candidate') {
            processNewCandidate(msgInput);
        }
    };

    return that;
};

export default BaseStack;



//////////////////
// WEBPACK FOOTER
// ./src/webrtc-stacks/BaseStack.js
// module id = 159
// module chunks = 0